import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { setError, setSuccess } from "../reducers/appSlice";
import PropTypes from "prop-types";
import moment from 'moment';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import DoneOutlineRounded from "@mui/icons-material/DoneOutlineRounded";
import Modal from "../components/Modal";
import ToggleButton from "@mui/material/ToggleButton";

import api from "../service/api";

const TableCellHead = styled(TableCell)(({ theme }) => ({
  color: "#3c4b64",
  padding: theme.spacing(1, 0, 1),
}));

const TextField = styled(MuiTextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const headCells = [
  {
    id: "expediente",
    numeric: true,
    disablePadding: true,
    label: "Expediente",
    align: "center",
    width: 200,
  },
  {
    id: "tramiteInterno",
    numeric: true,
    disablePadding: false,
    label: "Trámite Interno",
    align: "center",
    width: 200,
  },  
  {
    id: "asunto",
    numeric: false,
    disablePadding: false,
    label: "Asunto",
    align: "left",
    width: 600,
  },
  {
    id: "folios",
    numeric: false,
    disablePadding: false,
    label: "Folios",
    align: "right",
    width: 50,
  },  
];

function EnhancedTableHead(props) {
  
  return (
    <TableHead>
      <TableRow>        
        {headCells.map((headCell) => (
          <TableCellHead
            key={headCell.id}
            align= {headCell.align}
            padding="normal"
            width={headCell.width}
          >            
            <b>{headCell.label}</b>              
          </TableCellHead>          
        ))}        
        <TableCellHead width={200} align="center">
          <b>Acciones</b>
        </TableCellHead>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const validationSchema = Yup.object().shape(
  {    
    ubicacion: Yup.string().required("Requerido").min(3, "Mínimo 3 caracteres"),    
    observacion: Yup.string().min(3, "Mínimo 3 caracteres").max(200, "Máximo 200 caracteres"),    
  },
);

const validationSchemaEdicion = Yup.object().shape(
  {    
    ubicacion: Yup.string().required("Requerido").min(3, "Mínimo 3 caracteres"),    
    observacion: Yup.string().min(3, "Mínimo 3 caracteres").max(200, "Máximo 200 caracteres"),    
    fecha: Yup.date().required("Requerido").typeError("Formato no válido").max(new Date(), "Fecha no válida"),
  },
);

const Archivar = (props) => {
  const [open, setOpen] = React.useState(false);

  const [submitting, setSubmitting] = useState(false);
  
  const [editFolios, setEditFolios] = useState(0);
  const [grabarFolios, setGrabarFolios] = useState(0);

  const [selected, setSelected] = React.useState([]);

  const [page, setPage] = React.useState(0);
  
  const [datos, setDatos] = useState([]);
  
  const [mostrarUbicacion, setMostrarUbicacion] = useState(false);
  const [ubicacion, setUbicacion] = useState("");
  const [observacion, setObservacion] = useState("");
  const [fecha, setFecha] = useState("");
  
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  
  const [resultado, setResultado] = useState("");
  const [openResultado, setOpenResultado] = React.useState(false);
  
  const dispatch = useDispatch();
  
  useEffect(() => {    
    setEditFolios(0);
    setGrabarFolios(0);
  }, [props.datos]);

    
  const handleOpen = () => {
    setMostrarUbicacion(false);    
    setDatos([]);    
    setDatos(props.datos.map((item) => {return {...item, foliosAux: item.folios}}));
    
    if (props.mostrar === "edicion" && props.datos[0].ubicacion) {
      setUbicacion(props.datos[0].ubicacion);      
    } else {
      setUbicacion("");
    }
    console.log("observacion: "+props.datos[0].observacion);
    if (props.mostrar === "edicion" && props.datos[0].observacion) {
      setObservacion(props.datos[0].observacion);
    } else {
      setObservacion("");
    }
    
    if (props.mostrar === "edicion" && props.datos[0].fechaArchivo) {
      setFecha(moment(props.datos[0].fechaArchivo, "YYYY-MM-DD").format('YYYY-MM-DD'));
    } else {
      setFecha("");
    }

    setEditFolios(0);
    setGrabarFolios(0);
    setSubmitting(false);
    formik.values.ubicacion="";
    for (let index = 0; index < props.datos.length; index++) {
      const elemento = props.datos[index];
      console.log("elemento.folios: "+elemento.folios);
      if (elemento.soporte === "PAPEL" || elemento.soporte === "EN PAPEL") {
        setMostrarUbicacion(true);
        console.log("puso el mostrar ubicación en true");
      }
    }
    setOpen(true);    
  };

  const handleClose = () => {
    setDatos([]);
    setUbicacion("");
    setObservacion("");
    setFecha("");
    setOpen(false);    
  };

  const handleCloseResultado = () => {
    setOpenResultado(false);
  };

  const handleChangeFolios = (event, index) => {    
    if (/^\d*$/.test(event.target.value)){          
      let nuevo = [];
      for (let indice = 0; indice < datos.length; indice++) {
        nuevo[indice] = datos[indice];
      }
      nuevo[index].folios = event.target.value;
      setDatos([...nuevo]);
    }
  };

  const quitar = (index) => {
    let nuevo = [...datos];
    nuevo.splice(index, 1);
    setDatos([...nuevo]);
  };

  const habilitarFolios = (valor, index) => {    
    // habilitar input con folios + boton aceptar + boton cancelar 
    // y poner el foco en el folio correspondiente
    
    setEditFolios(valor);
    setGrabarFolios(valor);
    
    const labelFolios = `folios-${index}`;       
    setTimeout(function foco() {
      document.getElementById(labelFolios).focus();        
    }, 100);
    
  };

  const aceptarFolios = (index) => {
    // deshabilitar input con folios + ocultar boton aceptar + ocultar boton cancelar + 
    // habilitar botón cambiar cantidad de folios (lapicito)
    // y actualizar el valor de folios    

    const labelFolios = `folios-${index}`;   
    if (parseInt(document.getElementById(labelFolios).value) >= datos[index].foliosAux) {
      datos[index].foliosAux = datos[index].folios;    
      setEditFolios(0);
      setGrabarFolios(0);        
    } else {      
      dispatch(setError("La cantidad de folios debe ser mayor o igual a la cantidad de folios actual."));
      setTimeout(function foco() {
        datos[index].folios = datos[index].foliosAux;
        setEditFolios(0);
        setGrabarFolios(0);       
      }, 300);   
    }

  };

  const cancelarFolios = (index) => {
    // deshabilitar input con folios + ocultar boton aceptar + ocultar boton cancelar + 
    // habilitar botón cambiar cantidad de folios (lapicito)
    datos[index].folios = datos[index].foliosAux;    
    setEditFolios(0);
    setGrabarFolios(0);    
  };
  
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = datos.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - datos.length) : 0;

  const formik = useFormik({
    initialValues: {
      ubicacion: ubicacion,
      observacion: observacion,
      fecha: fecha,
    },    
    enableReinitialize: true,
    validationSchema: mostrarUbicacion ? (props.mostrar === "edicion" ? validationSchemaEdicion : validationSchema) : null,
    onSubmit: (values) => {   
      setSubmitting(true);

      let expedientesSeleccionados = [];
      for (let index = 0; index < datos.length; index++) {
        const elemento = datos[index];
        let cantidadFolios = "0";
        elemento.folios !== "-" ? cantidadFolios = elemento.folios : cantidadFolios = "0";
        let datoExpediente = {
          TRA_ID: elemento.id,
          CANT_FOLIOS: cantidadFolios,
        };
        expedientesSeleccionados.push(datoExpediente);        
      }

      let ubicacionFisica = "";
      formik.values.ubicacion !== "" ? ubicacionFisica = formik.values.ubicacion : ubicacionFisica = null;

      let observacionArchivo = "";
      formik.values.observacion !== "" ? observacionArchivo = formik.values.observacion : observacionArchivo = null;

      let datosArchivar = {};
      props.mostrar === "edicion" ? (
        datosArchivar = {        
          tramites: expedientesSeleccionados,
          ubicacionFisica: ubicacionFisica,
          observacion: observacionArchivo,
          fechaArchivo: formik.values.fecha,
        }
      ) : (
        datosArchivar = {        
          tramites: expedientesSeleccionados,
          ubicacionFisica: ubicacionFisica,
          observacion: observacionArchivo,
        }
      )

      props.mostrar === "edicion" ? editarArchivado(datosArchivar) : altaArchivado(datosArchivar);    
    },
  });

  const altaArchivado = (filtros) => {
    api
      .registrarArchivado(filtros)
      .then((res) => {  
        console.log("res: "+res.data);      
        verResultado(res.data);          
      })
      .catch((e) => {
        console.log("error: "+e.response.data.detalle);
        dispatch(setError(e.response.data.error));
      })
      .finally(() => {
        props.resultado();        
        handleClose();        
      });    
  };

  const editarArchivado = (filtros) => {
    console.log("Entró por editar archivado");
    api
      .editarArchivado(filtros)
      .then((res) => {  
        console.log("res: "+res.data);      
        //verResultado(res.data);          
        if (res.data.includes("Se modificó")) {
          dispatch(setSuccess(res.data));
        } else {
          dispatch(setError(res.data));
        }
      })
      .catch((e) => {
        console.log("error: "+e.response.data.detalle);
        dispatch(setError(e.response.data.error));
      })
      .finally(() => {
        props.resultado();        
        handleClose();        
      });    
  };

  const verResultado = (resultado) => {
    setResultado(resultado);
    setOpenResultado(true);
  };

  return (
    <div>         
      {props.mostrar === "boton" ? (
        <Button variant="gris" onClick={handleOpen} sx={{m: 1}}>
          Archivar
        </Button>
      ) : ( props.mostrar === "edicion" ? (
          <IconButton
            title="Editar"
            size="small"
            onClick={handleOpen}                              
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          ) : (
            <ToggleButton
              value="archivar"
              title="Archivar"
              disabled={props.numSelected === 0 || props.submitting}
              onClick={handleOpen}
              sx={{border:0, "&.Mui-disabled": {border: 0,}}}
            >
              <ArchiveOutlinedIcon />        
            </ToggleButton>
          ))}

      <Modal
        open={open}
        handleClose={handleClose}
        size="lg"        
      >
        <form
          id="archivar"
          name="archivar"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} sx={{ marginBottom: 1}}>
              <Typography variant="titulo">REGISTRAR ARCHIVADOS</Typography>
            </Grid>
            <Grid item xs={12} sm={12} sx={ mostrarUbicacion?{height: 90}:{height: 0}}>
              <TextField
                id="ubicacion"
                name="ubicacion"
                label="Ubicación física donde se archiva" 
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                multiline
                inputProps={{ maxLength: 200 }}
                rows={2}
                value={formik.values.ubicacion}
                onChange={formik.handleChange}
                error={
                  formik.touched.ubicacion &&
                  Boolean(formik.errors.ubicacion)
                }                
                sx={{ pl: 0, margin:0, visibility: mostrarUbicacion?'visible':'hidden' }}
                disabled={mostrarUbicacion? false : true }
              />
              <FormHelperText sx={{ padding: 0, margin:0, visibility: mostrarUbicacion?'visible':'hidden' }}>
                {formik.touched.ubicacion && formik.errors.ubicacion || "Máximo 200 caracteres"}
              </FormHelperText>
            </Grid>                      
            <Grid item xs={12} sm={12} sx={{height: 80}}>
              <TextField
                id="observacion"
                name="observacion"
                label="Observaciones" 
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
                multiline
                inputProps={{ maxLength: 200 }}
                rows={2}
                value={formik.values.observacion}
                onChange={formik.handleChange}
                error={
                  formik.touched.observacion &&
                  Boolean(formik.errors.observacion)
                }                
                sx={{ pl: 0, margin: 0 }}
              />
              <FormHelperText sx={{ padding: 0, margin:0 }}>
                { formik.touched.observacion && formik.errors.observacion || "Máximo 200 caracteres" }
              </FormHelperText>
            </Grid>

            { props.mostrar === "edicion" ? (
              <Grid item xs={12} lg={3} sx={{ mb: 1 }}>
                <FormControl
                  variant="outlined"
                  size="small"
                  error={
                    formik.touched.fecha && Boolean(formik.errors.fecha)
                  }
                  fullWidth
                >
                  <TextField
                    id="fecha"
                    name="fecha"
                    type="date"
                    value={formik.values.fecha}                    
                    label={props.mostrar === "edicion" ? "Archivado el" : "Archivados el"}
                    variant="outlined"
                    size="small"
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }} 
                    sx={{ pl: 0, mt:2, mb:0, ml:0, mr:0 }}/>
                  <FormHelperText>
                    {formik.touched.fecha && formik.errors.fecha}
                  </FormHelperText>
                </FormControl>
              </Grid>
            ) : ( null )
            }
            <Grid item xs={12} sm={12} sx={props.mostrar === "edicion" ? {marginTop: 0} : {marginTop: 2} }>
              <TableContainer>
                <Table 
                  sx={{ minWidth: 750, border: "1px solid #dee2e6" }}
                  aria-labelledby="tableTitle"
                  size="small"
                >
                  <EnhancedTableHead
                    numSelected={selected.length}
                    onSelectAllClick={handleSelectAllClick}
                    rowCount={datos.length}
                  />
                  <TableBody>                    
                      {
                      datos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((dato, index) => {
                        const isItemSelected = isSelected(dato.id);
                        const labelId = `enhanced-table-checkbox-${index}`;
                        const labelFolios = `folios-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={dato.id}
                            selected={isItemSelected}
                            sx={
                              index % 2 === 0
                                ? { backgroundColor: "#f2f2f2" }
                                : { backgroundColor: "#fff" }
                            }
                          >
                            <TableCell 
                              color="primary"
                              inputprops={{
                                "aria-labelledby": labelId,
                              }}
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                            >
                              {dato.expedienteConFecha}
                            </TableCell>
                            <TableCell align="left">
                              {dato.fechaTramiteInterno !== "" && dato.fechaExpediente !== "" && dato.fechaTramiteInterno !== dato.fechaExpediente ? dato.tramiteInternoConFecha : null}
                            </TableCell>                            
                            <TableCell align="left" sx={{ pl: 0, maxWidth: 600, whiteSpace: "nowrap", overflow: "hidden",textOverflow: 'ellipsis'}}>{dato.asunto}</TableCell>    
                            <TableCell align="right">
                              { (dato.soporte==="PAPEL" || dato.soporte==="EN PAPEL") && (props.mostrar === "edicion" || dato.recibido==="Sí" || dato.recibido==="X" || dato.recibido==="No (desglose)") ?
                                  <TextField
                                    id={labelFolios}
                                    name={labelFolios}
                                    type="text"                                    
                                    variant="standard"
                                    size="small"
                                    fullWidth 
                                    inputProps={{ maxLength: 5 , style: { textAlign: 'right' }}}
                                    value={dato.folios}
                                    sx={{p: 0, ml: 0, width: 60}}
                                    onChange={(event) => handleChangeFolios(event, index)}
                                    disabled={editFolios===dato.id ? false : true}
                                  />
                                : <InputLabel id="datoFolios" sx={{mr: 2}}>
                                    {dato.folios}
                                  </InputLabel>
                              }
                            </TableCell>
                            <TableCell align="center">
                              <Grid container spacing={1} align="left">
                                <Grid item align="left">
                                  <IconButton
                                    sx={{
                                    display: "flex",
                                    flexDirection: "row" }}
                                    title="Quitar de la lista"
                                    size="small"
                                    onClick={() => quitar(index)}
                                  >                                  
                                    <RemoveCircleOutlineOutlinedIcon />
                                  </IconButton>
                                </Grid >
                              { (grabarFolios === 0) ?
                                  <Grid item align="left">
                                    <IconButton
                                      sx={{ visibility: ((dato.soporte==="PAPEL" || dato.soporte==="EN PAPEL") && (props.mostrar === "edicion" || dato.recibido==="Sí" || dato.recibido==="X" || dato.recibido==="No (desglose)")) ?'visible':'hidden', 
                                        display: "flex",
                                        flexDirection: "row" }}
                                      title="Cambiar cantidad de folios"
                                      size="small"
                                      onClick={() => habilitarFolios(dato.id, index)}
                                    >                                  
                                      <EditOutlinedIcon />
                                    </IconButton>
                                  </Grid >
                                :
                                  <Grid item align="left">
                                    <Grid container spacing={1} align="left">
                                      <Grid item align="left">
                                        <IconButton
                                          sx={{ visibility: ( grabarFolios === dato.id ) ?'visible':'hidden', 
                                            display: "flex",
                                            flexDirection: "row" }}
                                          title="Aceptar"
                                          size="small"
                                          onClick={() => aceptarFolios(index)}                                          
                                        >                                  
                                          <CheckCircleOutlineOutlinedIcon />
                                        </IconButton>
                                      </Grid>
                                      <Grid item align="left">
                                        <IconButton
                                          sx={{ visibility: ( grabarFolios === dato.id ) ?'visible':'hidden', 
                                            display: "flex",
                                            flexDirection: "row" }}
                                          title="Cancelar"
                                          size="small"
                                          onClick={() => cancelarFolios(index)}
                                        >                                  
                                          <CancelOutlinedIcon />
                                        </IconButton>                                        
                                      </Grid>
                                    </Grid>
                                  </Grid>
                              }
                              </Grid>
                            </TableCell>                          
                          </TableRow>
                        );
                      })}
                    {datos.length === 0 && !submitting ? (
                      <TableRow>
                      <TableCell colSpan="8" align="center">
                        <Typography variant="aclaracion">No hay ningún trámite para archivar</Typography>
                      </TableCell>
                    </TableRow>
                    ) : (
                      null
                    )}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{
                          height: 53 * emptyRows,
                        }}
                      >
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[
                          5,
                          10,
                          25,
                          { label: "Todos", value: datos.length },
                        ]}                        
                        count={datos.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        selected={selected}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Grid>

            {/* Botones */}
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              <Button
                variant="plata"
                onClick={handleClose}
                endIcon={<ClearIcon />}
                sx={{ m: 1 }}
              >
                {"Cancelar"}
              </Button>{" "}
              <Button
                sx={{ m: 1 }}
                variant="verde"
                color="primary"
                type="submit"
                disabled={datos.length === 0 || submitting || editFolios !== 0}
                endIcon={<DoneOutlineRounded />}
              >
                {submitting ? "Confirmando..." : "Confirmar"}
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              align="right"
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <IconButton color="primary" onClick={handleClose} title="Cancelar">
                <ClearIcon />
              </IconButton>{" "}
              <IconButton
                color="primary"
                type="submit"
                disabled={datos.length === 0 || submitting || editFolios !== 0}
                title="Confirmar"
              >
                <DoneOutlineRounded />
              </IconButton>
            </Grid>

            <Grid item xs={12} sm={12}>
              {" "}
            </Grid>
          </Grid>
        </form>
      </Modal>

      <Modal
        open={openResultado}
        handleClose={handleCloseResultado}
        size="sm"
      >
        {resultado.split("\n").map((i, key) => {          
          return <Typography  variant="cuerpo" paragraph key={key}>{i}</Typography>;
        })}        
        <Box
          sx={{
            display: "flex",
            flexDirection: "row-reverse",
          }}
        >
          <Button
            sx={{ m: 1 }}
            variant="plata"
            onClick={handleCloseResultado}
            endIcon={<ClearIcon />}
          >
            {"Cerrar"}
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default Archivar;